import { dueCountdownForInvoice } from "../domain/dueDates";
import {
  formatInvoiceDate,
  formatInvoiceNumber,
  formatMoney,
} from "../domain/invoiceFormat";
import { invoiceStorageKey } from "../domain/invoiceIdentity";
import { statusLabel as invoiceStatusLabel } from "../domain/invoicesGroup";
import { lineItemsSummary } from "../domain/gst";
import { quoteStorageKey } from "../domain/quoteIdentity";
import {
  daysSinceSent,
  docKindLabel,
  statusLabel as quoteStatusLabel,
} from "../domain/quotesGroup";
import { esc } from "../lib/escape";
import { formatScheduledAt } from "./sendSheet";

function searchQFromNumber(raw: unknown): string {
  const n = parseInt(String(raw ?? ""), 10);
  return Number.isFinite(n) && n > 0 ? String(n) : String(raw || "").trim();
}

export function sourceQuoteMetaHtml(
  inv: Record<string, unknown>,
  quotes: Record<string, Record<string, unknown>>
): string {
  const id = String(inv.source_quote_id || "").trim();
  let num = inv.source_quote_number;
  let sourceQuote = id && quotes[id] && !quotes[id].deleted_at ? quotes[id] : null;

  if (!sourceQuote) {
    const invKey = invoiceStorageKey(inv);
    const invNum = Number(inv.invoice_number);
    sourceQuote =
      Object.values(quotes).find(
        (q) =>
          !q.deleted_at &&
          String(q.status || "") === "converted" &&
          (String(q.converted_invoice_id || "") === invKey ||
            (q.converted_invoice_number != null &&
              Number(q.converted_invoice_number) === invNum))
      ) || null;
  }

  if (sourceQuote) {
    num = sourceQuote.quote_number;
  } else if (num == null || num === "") {
    return id ? `<div class="meta">From quote (removed)</div>` : "";
  }

  if (num == null || num === "") return "";
  const display = formatInvoiceNumber(num as string | number);
  const q = searchQFromNumber(num);
  if (sourceQuote && q) {
    return `<div class="meta"><button type="button" class="meta-link" data-action="goto-quote" data-q="${esc(q)}">From quote #${display} ›</button></div>`;
  }
  return `<div class="meta">From quote #${display}</div>`;
}

export function convertedInvoiceMetaHtml(
  quote: Record<string, unknown>,
  invoices: Record<string, Record<string, unknown>>
): string {
  const id = String(quote.converted_invoice_id || "").trim();
  let num = quote.converted_invoice_number;
  let exists = false;
  if (id && invoices[id] && !invoices[id].deleted_at) {
    exists = true;
    if (num == null || num === "") num = invoices[id].invoice_number;
  } else if (num != null && num !== "") {
    const target = Number(num);
    exists = Object.values(invoices).some(
      (inv) => !inv.deleted_at && Number(inv.invoice_number) === target
    );
  } else if (id) {
    return `<div class="meta">Converted to invoice (removed)</div>`;
  } else {
    return "";
  }
  if (num == null || num === "") return "";
  const display = formatInvoiceNumber(num as string | number);
  const q = searchQFromNumber(num);
  if (exists && q) {
    return `<div class="meta"><button type="button" class="meta-link" data-action="goto-invoice" data-q="${esc(q)}">Converted to invoice #${display} ›</button></div>`;
  }
  return `<div class="meta">Converted to invoice #${display}</div>`;
}

export type InvoiceSummaryCardOpts = {
  settings?: Record<string, unknown>;
  multiBiz?: boolean;
  quotes?: Record<string, Record<string, unknown>>;
  /** When false, omit the customer name line (e.g. on a customer profile). Default true. */
  showCustomer?: boolean;
  /** Include send/paid/pdf action buttons. Default true. */
  showActions?: boolean;
};

export function invoiceSummaryCardHtml(
  inv: Record<string, unknown>,
  opts: InvoiceSummaryCardOpts = {}
): string {
  const settings = opts.settings || {};
  const multiBiz = Boolean(opts.multiBiz);
  const quotes = opts.quotes || {};
  const showCustomer = opts.showCustomer !== false;
  const showActions = opts.showActions !== false;

  const n = Number(inv.invoice_number);
  const id = esc(invoiceStorageKey(inv));
  const status = String(inv.status || "not_sent");
  const countdown =
    status === "sent" ? dueCountdownForInvoice(inv, new Date(), settings) : null;
  const canSend = ["not_sent", "send_failed"].includes(status);
  const isScheduled = status === "scheduled";
  const scheduledLabel = isScheduled ? formatScheduledAt(inv.scheduled_at) : "";
  const hasEmail = String(inv.customer_email || "").includes("@");
  const primaryActions = showActions
    ? [
        canSend
          ? `<button class="btn small primary" data-action="send" data-id="${id}">Send</button>`
          : "",
        isScheduled
          ? `<button class="btn small primary" data-action="send-now" data-id="${id}">Send now</button>
             <button class="btn small secondary" data-action="cancel-schedule" data-id="${id}">Cancel schedule</button>`
          : "",
        status === "sent"
          ? `<button class="btn small primary" data-action="paid" data-id="${id}">Mark paid</button>
             <button class="btn small secondary" data-action="followup" data-id="${id}" ${
               hasEmail ? "" : "disabled title=\"Add a customer email to follow up\""
             }>Follow up</button>`
          : "",
      ]
        .filter(Boolean)
        .join("")
    : "";

  return `<article class="card card-click" data-open-invoice="${id}" data-inv="${id}">
    <div class="card-top">
      <div class="card-top-main">
        <strong>#${formatInvoiceNumber(n)}</strong>
        <span class="badge badge-${status}">${invoiceStatusLabel(status)}</span>
      </div>
      ${
        showActions
          ? `<button type="button" class="btn small ghost card-more" data-action="more" data-id="${id}" data-n="${n}" aria-label="More actions">···</button>`
          : ""
      }
    </div>
    <div class="meta">${formatInvoiceDate(String(inv.invoice_date || ""))}${
      showCustomer ? ` · ${esc(String(inv.customer_name || ""))}` : ""
    }</div>
    ${multiBiz && inv.business_name ? `<div class="meta">From: ${esc(String(inv.business_name))}</div>` : ""}
    <div class="meta">${formatMoney(Number(inv.total_inc_gst || 0))} · ${esc(lineItemsSummary(inv))}</div>
    ${
      scheduledLabel
        ? `<div class="meta">Scheduled for ${esc(scheduledLabel)}</div>`
        : ""
    }
    ${
      countdown
        ? `<div class="meta countdown-${countdown.kind}">${countdown.label} (${countdown.due_date_fmt})</div>`
        : ""
    }
    ${sourceQuoteMetaHtml(inv, quotes)}
    ${primaryActions ? `<div class="actions">${primaryActions}</div>` : ""}
  </article>`;
}

export type QuoteSummaryCardOpts = {
  multiBiz?: boolean;
  invoices?: Record<string, Record<string, unknown>>;
  showCustomer?: boolean;
  showActions?: boolean;
};

export function quoteSummaryCardHtml(
  quote: Record<string, unknown>,
  opts: QuoteSummaryCardOpts = {}
): string {
  const multiBiz = Boolean(opts.multiBiz);
  const invoices = opts.invoices || {};
  const showCustomer = opts.showCustomer !== false;
  const showActions = opts.showActions !== false;

  const n = Number(quote.quote_number);
  const id = esc(quoteStorageKey(quote));
  const status = String(quote.status || "not_sent");
  const kind = docKindLabel(String(quote.doc_kind || "quote"));
  const canSend = ["not_sent", "send_failed"].includes(status);
  const isScheduled = status === "scheduled";
  const scheduledLabel = isScheduled ? formatScheduledAt(quote.scheduled_at) : "";
  const days = status === "sent" ? daysSinceSent(quote) : null;
  const daysLabel =
    days == null
      ? ""
      : days === 0
        ? "Sent today"
        : days === 1
          ? "Sent 1 day ago"
          : `Sent ${days} days ago`;
  const primaryActions = showActions
    ? [
        canSend
          ? `<button class="btn small primary" data-action="send" data-id="${id}">Send</button>`
          : "",
        isScheduled
          ? `<button class="btn small primary" data-action="send-now" data-id="${id}">Send now</button>
             <button class="btn small secondary" data-action="cancel-schedule" data-id="${id}">Cancel schedule</button>`
          : "",
        status === "sent"
          ? `<button class="btn small primary" data-action="convert" data-id="${id}">Create invoice</button>
             <button class="btn small secondary" data-action="close" data-id="${id}">Close</button>`
          : "",
      ]
        .filter(Boolean)
        .join("")
    : "";

  return `<article class="card card-click" data-open-quote="${id}" data-quote="${id}">
    <div class="card-top">
      <div class="card-top-main">
        <strong>${esc(kind)} #${formatInvoiceNumber(n)}</strong>
        <span class="badge badge-${status}">${quoteStatusLabel(status)}</span>
      </div>
      ${
        showActions
          ? `<button type="button" class="btn small ghost card-more" data-action="more" data-id="${id}" data-n="${n}" aria-label="More actions">···</button>`
          : ""
      }
    </div>
    <div class="meta">${formatInvoiceDate(String(quote.quote_date || ""))}${
      showCustomer ? ` · ${esc(String(quote.customer_name || ""))}` : ""
    }</div>
    ${multiBiz && quote.business_name ? `<div class="meta">From: ${esc(String(quote.business_name))}</div>` : ""}
    <div class="meta">${formatMoney(Number(quote.total_inc_gst || 0))} · ${esc(lineItemsSummary(quote))}</div>
    ${scheduledLabel ? `<div class="meta">Scheduled for ${esc(scheduledLabel)}</div>` : ""}
    ${daysLabel ? `<div class="meta">${esc(daysLabel)}</div>` : ""}
    ${status === "converted" ? convertedInvoiceMetaHtml(quote, invoices) : ""}
    ${primaryActions ? `<div class="actions">${primaryActions}</div>` : ""}
  </article>`;
}

/** Collapsible group wrapping a pre-rendered list of cards. */
export function docGroupHtml(
  title: string,
  count: number,
  innerHtml: string,
  { open = false, emptyHint = "None" }: { open?: boolean; emptyHint?: string } = {}
): string {
  if (!count) {
    return `<details class="invoice-group"${open ? " open" : ""}><summary>${esc(title)} (0)</summary><p class="hint">${esc(emptyHint)}</p></details>`;
  }
  return `<details class="invoice-group"${open ? " open" : ""}>
    <summary>${esc(title)} (${count})</summary>
    ${innerHtml}
  </details>`;
}

export function invoiceSummaryListHtml(
  items: Record<string, unknown>[],
  opts: InvoiceSummaryCardOpts = {}
): string {
  return items.map((inv) => invoiceSummaryCardHtml(inv, opts)).join("");
}

export function quoteSummaryListHtml(
  items: Record<string, unknown>[],
  opts: QuoteSummaryCardOpts = {}
): string {
  return items.map((q) => quoteSummaryCardHtml(q, opts)).join("");
}
